import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	logOutLocal,
	updateTokens,
	selectAccessToken,
	selectRefreshToken,
	selectAccountMembershipId,
} from '@shared/api/reduxToolkit';
import { isConsentCallbackRoute, isConsentPopupWindow } from '@shared/lib/utils';

// Proactive token refresh on app startup (after page reload).
//
// Problem: accessToken lives in memory only (not persisted).
// After a page reload Redux rehydrates from sessionStorage — refreshToken is restored,
// but accessToken is null. Without this initializer the user would hit 401 on the
// first API request and only get a new accessToken after baseQueryWithReauth fires.
//
// This component runs once on mount, checks if a refresh is needed, and silently
// obtains a new accessToken before any API requests go out.
export const AuthInitializer = ({ children }) => {
	const dispatch = useDispatch();
	const accessToken = useSelector(selectAccessToken);
	const refreshToken = useSelector(selectRefreshToken);
	const userId = useSelector(selectAccountMembershipId);

	// React StrictMode (dev only) simulates unmount/remount — useEffect fires twice.
	// Without this guard two refresh requests fire simultaneously: the backend uses token rotation,
	// so the first returned refreshToken gets immediately invalidated by the second request.
	const processedRef = useRef(false);

	useEffect(() => {
		if (processedRef.current) return;
		processedRef.current = true;

		// Skip inside a consent popup: its sessionStorage is a copy of the opener's (refreshToken
		// included), so refreshing here would rotate the shared token and could log the original tab
		// out. The popup only loads the consent-callback route to relay a message and close.
		// The route check covers popups whose window.name was cleared on the way back (see
		// isConsentCallbackRoute) — without it the popup rotated the token and the admin got logged out.
		if (isConsentPopupWindow() || isConsentCallbackRoute()) return;

		// Skip if: access token already in memory (normal flow), or no refresh token (not logged in)
		if (accessToken) return;
		if (!refreshToken) return;
		if (!userId) return;

		fetch('/api/v1/user/refreshToken', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ refreshToken, userId }),
		})
			.then((r) => {
				if (!r.ok) throw new Error(`Refresh failed: ${r.status}`);
				return r.json();
			})
			.then((data) => {
				dispatch(updateTokens(data));
			})
			.catch(() => {
				dispatch(logOutLocal());
			});
	// Runs once on mount — deps intentionally omitted
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return children;
};
